<template>
    <div class="tag-input" :class="{ disabled: disabled, 'is-focused': focused }" @click="focusEntry">
        <span v-for="(tag, i) in modelValue" :key="`${tag}-${i}`" class="tag-chip" :class="variant">
            {{ tag }}
            <button v-if="!disabled" type="button" class="tag-remove" :aria-label="`Remove ${tag}`"
                @click.stop="remove(i)">
                <i class="fa fa-xmark"></i>
            </button>
        </span>
        <input v-if="!disabled" ref="entry" v-model="entry" type="text" class="tag-entry"
            :placeholder="modelValue.length ? '' : placeholder" @keydown.enter.prevent="commit"
            @keydown.,.prevent="commit" @keydown.delete="backspace" @blur="commit" @focus="focused = true" />
        <span v-else-if="!modelValue.length" class="tag-empty">—</span>
    </div>
</template>

<script>
/**
 * Chip entry for the schema's many `synonyms` / `tags` string lists.
 *
 * Commits on Enter, comma, or blur — blur included because these sit inside a
 * form where the natural next action is clicking the next field, and a
 * half-typed synonym silently vanishing is the more annoying failure.
 */
export default {
    name: 'TagInput',
    props: {
        modelValue: { type: Array, default: () => [] },
        placeholder: { type: String, default: 'Type and press Enter' },
        disabled: { type: Boolean, default: false },
        /** `synonym` reads as an alias, `tag` as a classification. */
        variant: { type: String, default: 'synonym' },
    },
    emits: ['update:modelValue'],
    data() {
        return { entry: '', focused: false };
    },
    methods: {
        focusEntry() {
            if (!this.disabled) this.$refs.entry?.focus();
        },
        commit() {
            this.focused = false;
            const value = this.entry.trim();
            this.entry = '';
            // Silently ignore duplicates — the list is a set in spirit.
            if (!value || this.modelValue.includes(value)) return;
            this.$emit('update:modelValue', [...this.modelValue, value]);
        },
        remove(index) {
            this.$emit('update:modelValue', this.modelValue.filter((_, i) => i !== index));
        },
        /** Backspace on an empty entry pops the last chip, as chip inputs do. */
        backspace() {
            if (this.entry === '' && this.modelValue.length) {
                this.$emit('update:modelValue', this.modelValue.slice(0, -1));
            }
        },
    },
};
</script>

<style scoped>
.tag-input {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.3rem;
    min-height: calc(1.5em + 0.5rem + 2px);
    padding: 0.25rem 0.4rem;
    border: 1px solid #d2ddec;
    border-radius: 0.375rem;
    background: #fff;
    cursor: text;
    transition: border-color 0.12s ease, box-shadow 0.12s ease;
}

.tag-input.is-focused {
    border-color: #2c7be5;
    box-shadow: 0 0 0 3px rgba(44, 123, 229, 0.1);
}

.tag-input.disabled {
    background: #f9fbfd;
    cursor: default;
}

.tag-chip {
    gap: 0.25rem;
    font-size: 0.7rem;
    font-weight: 500;
    padding: 0.12rem 0.4rem;
    border-radius: 0.25rem;
    white-space: nowrap;
}

.tag-chip.synonym {
    background: #e8f1fd;
    color: #2c7be5;
}

.tag-chip.tag {
    background: #edf2f9;
    color: #6e84a3;
}

.tag-remove {
    border: 0;
    background: transparent;
    padding: 0;
    line-height: 1;
    color: inherit;
    opacity: 0.55;
    font-size: 0.65rem;
}

.tag-remove:hover {
    opacity: 1;
}

.tag-entry {
    flex: 1;
    min-width: 6rem;
    border: 0;
    outline: 0;
    background: transparent;
    font-size: 0.78rem;
    color: #12263f;
    padding: 0.1rem 0;
}

.tag-entry::placeholder {
    color: #b1c2d9;
}

.tag-empty {
    font-size: 0.78rem;
    color: #b1c2d9;
}
</style>

<style lang="scss">
@use '@/theme/scss/_user-variables.scss' as *;

body.dark-mode {
    .tag-input {
        background: $dark-input-bg !important;
        border-color: $dark-border-color !important;
    }

    .tag-input.disabled {
        background: $dark-body-bg !important;
    }

    .tag-chip.synonym {
        background: $dark-selected-bg !important;
        color: #7ab0ff !important;
    }

    .tag-chip.tag {
        background: $dark-elevated-bg !important;
        color: $dark-text-secondary !important;
    }

    .tag-entry {
        color: $dark-text-primary !important;
    }
}
</style>
